/**
 * CatchScreen
 *
 * The 3-phase fish reveal animation screen.
 * Extracted from App.js lines 1180–1290.
 *
 * Props:
 *   currentCatch {object}   - The fish object from useCatchAnimation.
 *   catchStep    {number}   - Animation phase: 0 | 1 | 2 | 3.
 *   setCatchStep {function} - Advances the animation phase.
 *   onKeep       {function} - Called with the fish when KEEP is clicked.
 *   onSell       {function} - Called with the fish when SELL is clicked.
 */

import { effectFilters, rarityGlows } from '../constants/effects';
import { tileFish, FISH_SPRITE_HEIGHTS, DEFAULT_CATCH_SPRITE_HEIGHT, coinIcon } from '../constants/assets';
import StarRating from './StarRating';

export default function CatchScreen({
  currentCatch,
  catchStep,
  setCatchStep,
  onKeep,
  onSell,
}) {
  if (!currentCatch) return null;

  const spriteHeight = FISH_SPRITE_HEIGHTS[currentCatch.id] ?? DEFAULT_CATCH_SPRITE_HEIGHT;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', width: '100%', zIndex: 300, backgroundColor: '#05070a', overflow: 'hidden' }}>

      {/* Name / Effect / Rarity Reveal */}
      <div style={{ height: '100px', display: 'flex', alignItems: 'flex-end', paddingBottom: '20px' }}>
        {catchStep >= 3 && (
          <h2
            className={`anim-fade glow-${currentCatch.effect}`}
            style={{ textAlign: 'center', fontSize: '18px', margin: 0, lineHeight: '1.4', textTransform: 'uppercase' }}
          >
            {currentCatch.effect !== 'default' && (
              <span
                className={`text-effect-${currentCatch.effect.toLowerCase()}`}
                style={{ fontSize: '14px', display: 'block', opacity: 0.8, color: '#ffffff' }}
              >
                {currentCatch.effect}
              </span>
            )}
            <span style={{ color: '#fff' }}>
              {currentCatch.rarity.replace(/_/g, ' ')} {currentCatch.name.replace(/_/g, ' ')}
            </span>
          </h2>
        )}
      </div>

      {/* SPRITE AREA */}
      <div style={{ width: '100%', maxWidth: '400px', height: '200px', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>

        {catchStep <= 1 && (
          <img
            src={tileFish}
            className={catchStep === 1 ? 'anim-shake' : ''}
            alt="puzzle tile"
            style={{ width: '100px', height: '100px', imageRendering: 'pixelated' }}
            onAnimationEnd={() => setCatchStep(2)}
          />
        )}

        {catchStep >= 2 && (
          <div
            key={currentCatch.instanceId ?? currentCatch.id}
            className="anim-grow"
            onAnimationEnd={() => setCatchStep(3)}
            style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%',
              filter: `drop-shadow(0 0 20px ${rarityGlows[currentCatch.rarity.toLowerCase()] || '#ffffff'})`,
            }}
          >
            <img
              src={`assets/fish/${currentCatch.id}.png`}
              alt={currentCatch.name}
              className={currentCatch.effect === 'prismatic' ? 'effect-prismatic' : ''}
              style={{
                width: 'auto',
                height: `${spriteHeight}px`,
                objectFit: 'contain',
                imageRendering: 'pixelated',
                filter: effectFilters[currentCatch.effect.toLowerCase()] || 'none',
              }}
              onError={(e) => { e.target.src = 'https://via.placeholder.com/100?text=FISH'; }}
            />
          </div>
        )}
      </div>

     {/* Stats + Action Buttons */}
      <div style={{ height: '150px', paddingTop: '40px', width: '100%', maxWidth: '300px' }}>
        {catchStep >= 3 && (
          <div className="anim-fade" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#161b22', padding: '10px', borderRadius: '6px', border: '1px solid #30363d' }}>
              <StarRating count={currentCatch.stars} size={20} />
              <span style={{ display: 'flex', alignItems: 'center', color: '#8b949e' }}>{currentCatch.weight}lbs</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#44ff44' }}>
                <img src={coinIcon} alt="gold" style={{ width: '20px', height: '20px', imageRendering: 'pixelated' }} />
                {currentCatch.sellPrice}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => onKeep(currentCatch)}
                style={{ flex: 1, padding: '15px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: '6px', fontFamily: 'inherit', cursor: 'pointer', fontSize: '12px' }}
              >
                KEEP
              </button>
              <button
                onClick={() => onSell(currentCatch)}
                style={{ flex: 1, padding: '15px', background: 'transparent', color: '#f1c40f', border: '2px solid #f1c40f', borderRadius: '6px', fontFamily: 'inherit', cursor: 'pointer', fontSize: '12px' }}
              >
                SELL
              </button>
            </div>

          </div>
        )}
      </div>

    </div>
  );
}